import { useGetUnitGenericDetails } from '@apps-common/apis/portal/unit'
import { UnitActivationsTableActions } from '@apps-common/apps/portal/pages/units/pages/activations/components/activations-table/UnitActivationsTableActions'
import { ImageComponent } from '@apps-common/components/common'
import { commonIcons } from '@apps-common/config/icons'
import { UnitActivationStatus } from '@apps-common/config/unit-activation'
import { toLocalTimestampStr } from '@apps-common/helpers/to-local-timestamp'
import { valueStringCheck } from '@apps-common/helpers/value-string-check'
import { IAppNavigation } from '@apps-common/hooks/useNavigation'
import { IActivationServiceRequestModel } from '@apps-common/types/portal/service-request'
import { ROUTE_UNIT_MANAGE } from '@apps-radionet-portal-root/src/config/private-routes'
import { UnitActivationStatusPill } from '../UnitActivationStatusPill'

export const buildTableKeys = (navigation: IAppNavigation) => [
  {
    title: '',
    key: 'productImageUrl',
    static: true,
    thClasses: 'bg-primary-content/[0.05]',
    tdClasses: 'bg-secondary text-secondary-content',
    Component: ({ productImageUrl }: IActivationServiceRequestModel) => (
      <ImageComponent
        src={productImageUrl}
        alt='Product image'
        containerClasses='bg-secondary-content/10 w-[36px] h-[36px] rounded-[15px] flex items-center justify-center'
        imageClasses='w-[28px] h-[28px]'
      />
    )
  },
  {
    title: 'Status',
    key: 'status',
    thClasses: 'bg-primary-content/[0.05] hidden lg:table-cell',
    tdClasses: 'bg-secondary text-secondary-content hidden lg:table-cell',
    Component: (props: IActivationServiceRequestModel) => (
      <div className='flex min-w-[150px]'>
        <UnitActivationStatusPill status={props.status || ''} />
      </div>
    )
  },
  {
    title: 'MCentre',
    key: 'mCentre',
    changeable: true,
    thClasses: 'bg-primary-content/[0.05]',
    tdClasses: 'bg-secondary text-secondary-content'
  },
  {
    title: 'Account',
    key: 'accountNo',
    changeable: true,
    thClasses: 'bg-primary-content/[0.05]',
    tdClasses: 'bg-secondary text-secondary-content',
    Component: ({ status, accountNo }: IActivationServiceRequestModel) => {
      if (status === UnitActivationStatus.cancelled) {
        return <span>{'<TBC>'}</span>
      }

      return <span>{valueStringCheck(accountNo)}</span>
    }
  },
  {
    title: 'IMEI',
    key: 'imei',
    changeable: true,
    thClasses: 'bg-primary-content/[0.05] hidden lg:table-cell',
    tdClasses: 'bg-secondary text-secondary-content hidden lg:table-cell'
  },
  {
    title: 'S/N',
    key: 'serialNo',
    changeable: true,
    thClasses: 'bg-primary-content/[0.05] hidden lg:table-cell',
    tdClasses: 'bg-secondary text-secondary-content hidden lg:table-cell'
  },
  {
    title: 'UpdatedAt',
    key: 'dateEdited',
    changeable: true,
    thClasses: 'bg-primary-content/[0.05] hidden lg:table-cell',
    tdClasses: 'bg-secondary text-secondary-content hidden lg:table-cell',
    Component: (props: IActivationServiceRequestModel) => (props?.dateEdited ? toLocalTimestampStr(props.dateEdited) : <commonIcons.dash />)
  },
  {
    title: 'Bureau',
    key: 'bureau',
    optional: true,
    thClasses: 'bg-primary-content/[0.05] hidden lg:table-cell',
    tdClasses: 'bg-secondary text-secondary-content hidden lg:table-cell'
  },
  {
    title: 'Status Desc',
    key: 'statusDescription',
    optional: true,
    thClasses: 'bg-primary-content/[0.05] hidden lg:table-cell',
    tdClasses: 'bg-secondary text-secondary-content hidden lg:table-cell'
  },
  {
    title: 'EditedBy',
    key: 'editedBy',
    optional: true,
    thClasses: 'bg-primary-content/[0.05] hidden lg:table-cell',
    tdClasses: 'bg-secondary text-secondary-content hidden lg:table-cell'
  },
  {
    title: 'ActionedBy',
    key: 'actionedBy',
    optional: true,
    thClasses: 'bg-primary-content/[0.05] hidden lg:table-cell',
    tdClasses: 'bg-secondary text-secondary-content hidden lg:table-cell'
  },
  {
    title: 'CreatedAt',
    key: 'dateCreated',
    optional: true,
    thClasses: 'bg-primary-content/[0.05] hidden lg:table-cell',
    tdClasses: 'bg-secondary text-secondary-content hidden lg:table-cell',
    Component: (props: IActivationServiceRequestModel) => (props?.dateEdited ? toLocalTimestampStr(props.dateEdited) : <commonIcons.dash />)
  },
  {
    title: 'Existing Device',
    key: 'existingDevice',
    optional: true,
    thClasses: 'bg-primary-content/[0.05]',
    tdClasses: 'bg-secondary text-secondary-content hidden xl:table-cell',
    Component: ({ existingUnitId }: IActivationServiceRequestModel) => {
      const { data, isLoading, isError } = useGetUnitGenericDetails({ config: { enabled: !!existingUnitId }, unitId: existingUnitId })

      if (!existingUnitId) {
        return (
          <span>
            <commonIcons.dash />
          </span>
        )
      }

      const loading = !data || isLoading || isError

      return (
        <>
          {loading && <div className='w-full h-[24px] bg-secondary-content/5 rounded-lg animate-pulse' />}
          {!loading && (
            <button
              type='button'
              aria-label='Navigate existing device'
              className='flex items-center justify-start gap-[5px]'
              onClick={() =>
                navigation.navigateForward({
                  routeUrl: ROUTE_UNIT_MANAGE.buildPath(data.status, data.unitId),
                  route: ROUTE_UNIT_MANAGE
                })
              }
            >
              <div>{data.accountNo}</div>
              <div className='bg-secondary-content/10 text-secondary-content rounded-full p-[5px]'>
                <commonIcons.arrowAngled />
              </div>
            </button>
          )}
        </>
      )
    }
  },
  {
    title: 'Actions',
    key: 'actions',
    static: true,
    thClasses: 'text-right bg-primary-content/[0.05]',
    tdClasses: 'text-right bg-secondary text-secondary-content',
    Component: (props: IActivationServiceRequestModel) => <UnitActivationsTableActions navigation={navigation} activation={props} />
  }
]
